<template>
  <div class="profiles-wrapper" v-if="canRender" :class="{'fetching': fetching}">
    <transition name="fade" mode="out-in">
      <div class="spinner-wrapper" v-if="fetching">
        <div class="spinner">
          <div class="bounce1"></div>
          <div class="bounce2"></div>
          <div class="bounce3"></div>
        </div>
      </div>
    </transition>
    <div
      v-for="profile in profiles"
      :key="profile.sys.id"
      class="profile-wrapper"
      :class="{'profile-is-active': showingProfile, 'this-profile-is-active': visible[profile.sys.id], 'opaque': !matchingEntries(profile)}"
    >
      <el-popover
        class="popover"
        placement="left"
        width="300"
        trigger="click"
        v-model="visible[profile.sys.id]"
      >
        <slot>
          <h3>{{profile.fields.name}}</h3>
          <div v-html="mdToHTML(profile.fields.description)"></div>
        </slot>
        <div slot="reference">
          <div class="icon-wrapper">
            <div
              :key="matchingEntries(profile) | filterNaN"
              class="avatar"
              :style="{backgroundImage: `url(${profile.fields.avatar.fields.file.url})`}"
            ></div>
          </div>
          <p class="profile-name">{{profile.fields.name}}</p>
        </div>
      </el-popover>
      <transition mode="out-in" name="bounce">
        <div
          :key="matchingEntries(profile) | filterNaN"
          class="percentage standOut"
        >{{matchingEntries(profile) | filterNaN}}%</div>
      </transition>
    </div>
  </div>
</template>

<script>
import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

export default {
  name: 'ProfileStats',

  props: ['entries', 'fetching'],

  data () {
    return {
      canRender: false,
      visible: {},
      showingProfile: false
    }
  },

  computed: {
    profiles () {
      return this.$store.state.testResults.profiles
    }
  },

  methods: {
    mdToHTML (markdown) {
      return documentToHtmlString(markdown)
    },
    matchingEntries (profile) {
      var filtered = this.entries.filter(entry => {
        return entry.calculatedProfile === profile.sys.id
      })
      return Math.round(filtered.length / this.entries.length * 1000 / 10)
    },
    getProfile (profileId) {
      var matchingProfile = this.profiles.filter(profile => {
        return profile.sys.id.toString() === profileId.toString()
      })
      return matchingProfile[0]
    },
    calculateProfiles () {
      this.canRender = false
      if (!this.profiles) {
        return {}
      }
      // Add the calculated profile to each entry
      this.entries.forEach(entry => {
        entry.calculatedProfile = this.calculateProfile(entry.weightedTestResults)
        entry.calculatedProfileObject = this.getProfile([entry.calculatedProfile])
      })

      // Add the score per user to a high-level prop
      this.entries.forEach(entry => {
        entry.calculatedScore = (entry.weightedTestResults[0] + entry.weightedTestResults[1]) / 2
      })

      this.canRender = true
    },
    calculateProfile (weightedTestResults) {
      if (!weightedTestResults || !this.profiles.length || !this.entries.length) {
        return null
      }
      const tr = weightedTestResults

      var outputProfile = ''
      this.$store.state.testResults.profiles.forEach((profile, i) => {
        var match = 0
        var minVector = profile.fields.weightedMin.split(',')
        var maxVector = profile.fields.weightedMax.split(',')
        minVector.forEach((variable, j) => {
          if (tr[j] >= minVector[j] && tr[j] <= maxVector[j]) match++
        })

        if (match === minVector.length) {
          outputProfile = Object.assign({}, profile)
        }
      })

      if (!outputProfile) {
        console.log('Warning, there is no profile defined for this score: ', tr)
        return
      }
      return outputProfile.sys.id
    }
  },

  filters: {
    filterNaN (value) {
      if (isNaN(value)) {
        return 0
      }
      return value
    }
  },

  mounted () {
    setTimeout(() => {
      this.calculateProfiles()
    }, 100)
  },

  watch: {
    'entries': function (newVal) {
      if (newVal.length) {
        setTimeout(() => {
          this.calculateProfiles()
        }, 100)
      }
    },
    visible: {
      handler (newVal) {
        if (JSON.stringify(newVal).indexOf('true') > -1) {
          this.showingProfile = true
        } else {
          this.showingProfile = false
        }
      },
      deep: true
    }
  }

}
</script>

<style scoped lang="scss">
.spinner-wrapper {
  transform: scale(2);
  width: 100%;
  height: 100%;
  position: absolute;
  margin-top: 10em;
  @include breakpoint($air) {
    margin-top: 6em;
  }
}

.profiles-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  position: relative;
  &.fetching {
    opacity: 0.8;
  }
}
.profile-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 90vw;
  margin: 2em 0;

  @include breakpoint($tablet) {
    min-width: 12em;
    margin: 0.2em 0;
  }

  @include breakpoint($desktop) {
    min-width: 6em;
    margin: 0.2em 0;
  }

  @include breakpoint($fullhd) {
    min-width: 8em;
  }

  .profile-name {
    font-size: 1.3em;
    margin-top: 0.5em;
    margin-bottom: 0em;
    @include breakpoint($tablet) {
      font-size: 0.7em;
    }
    @include breakpoint($desktop) {
      font-size: 0.6em;
    }
  }
  .percentage {
    font-size: 1.3em;
    @include breakpoint($tablet) {
      font-size: 1em;
      margin-bottom: 0.4em;
    }
  }

  .icon-wrapper {
    width: 45vw;
    height: 0;
    margin: auto;
    padding-bottom: 45vw;
    background: $gray-light;
    border-radius: 10em;
    @include breakpoint($tablet) {
      width: 8em;
      padding-bottom: 8em;
      min-width: 8em;
    }
    @include breakpoint($desktop) {
      width: 3.5em;
      padding-bottom: 3.5em;
      min-width: 3.5em;
    }

    @include breakpoint($fullhd) {
      width: 6em;
      padding-bottom: 6em;
    }
    .avatar {
      width: 100%;
      height: 0;
      padding-bottom: 75%;
      background-size: cover;
      background-position: top center;
    }
    &:hover {
      cursor: pointer;
    }
  }
}

.profile-wrapper {
  transition: opacity 0.2s ease-in-out;
  opacity: 0.8;
  &.profile-is-active {
    opacity: 0.1;
    &.this-profile-is-active {
      opacity: 1;
    }
  }
  &.opaque {
    opacity: 0.05;
  }
  &:hover {
    opacity: 1;
  }
}
</style>
